import { AssetStatus } from '@rematter/frontend-lib-api-client';

import { type MapMarker } from '~/shared/components/MapView/MapView';
import { createQueryHook } from '~/shared/providers/api';

const FACILITY_COLOR = '#2768E7';
const ACCOUNT_ADDRESS_COLOR = '#16A34A';

export const useMapFacilitiesQuery = createQueryHook(
  'transportation.mapFacilities',
  () => ({
    facilities: {
      __args: {
        pagination: { limit: 1000, offset: 0 },
      },
      entries: {
        id: true,
        facilityName: true,
        address: {
          city: true,
          state: true,
          coordinates: {
            latitude: true,
            longitude: true,
          },
        },
      },
      page: { total: true },
    },
  }),
  {
    select: (data): MapMarker[] =>
      data?.facilities.entries
        .filter(
          (f) =>
            f.address?.coordinates?.latitude &&
            f.address?.coordinates?.longitude,
        )
        .map((f) => ({
          id: `facility-${f.id}`,
          name: f.facilityName,
          lat: Number(f.address?.coordinates?.latitude),
          lng: Number(f.address?.coordinates?.longitude),
          color: FACILITY_COLOR,
        })) ?? [],
  },
);

export const useMapAccountAddressesQuery = createQueryHook(
  'transportation.mapAccountAddresses',
  () => ({
    accountAddresses: {
      __args: {
        filter: { searchTerm: '' },
        pagination: { limit: 1000, offset: 0 },
      },
      entries: {
        id: true,
        name: true,
        address: {
          city: true,
          state: true,
          coordinates: {
            latitude: true,
            longitude: true,
          },
        },
        account: {
          id: true,
          name: true,
        },
      },
    },
  }),
  {
    select: (data): MapMarker[] =>
      data?.accountAddresses.entries
        .filter(
          (e) =>
            e.address?.coordinates?.latitude &&
            e.address?.coordinates?.longitude,
        )
        .map((e) => ({
          id: `account-${e.id}`,
          name: e.name || e.account.name,
          lat: Number(e.address?.coordinates?.latitude),
          lng: Number(e.address?.coordinates?.longitude),
          color: ACCOUNT_ADDRESS_COLOR,
        })) ?? [],
  },
);

export type MapAsset = {
  identifier: string;
  status: AssetStatus;
  facilityId: string | null;
  accountAddressId: string | null;
};

export const useMapAssetsQuery = createQueryHook(
  'transportation.mapAssets',
  () => ({
    assets: {
      __args: {
        pagination: { limit: 1000, offset: 0 },
      },
      entries: {
        identifier: true,
        status: true,
        facility: { id: true },
        accountAddress: { id: true },
      },
      page: { total: true },
    },
  }),
  {
    select: (data): MapAsset[] =>
      data?.assets.entries.map((a) => ({
        identifier: a.identifier,
        status: a.status,
        facilityId: a.facility?.id ?? null,
        accountAddressId: a.accountAddress?.id ?? null,
      })) ?? [],
  },
);
